<script setup lang="ts">
import { ref, onMounted } from "vue"

import QRCodeVue from "qrcode.vue"
import ValidationMessage from "@Modules/shared/Components/ValidationMessage.vue"

//
// Props
interface Props {
  getQrCodeUrl: () => Promise<string | null>
  verifyTwoFactor: (params: { token: string }) => Promise<boolean>
  notifyVerified: () => void
}

const props = defineProps<Props>()

//
// Emit
interface Events {
  (e: "close"): void
}
const emit = defineEmits<Events>()

//
// Data
const url = ref<string | null>(null)
const token = ref("")
const tokenInput = ref<HTMLInputElement | null>(null)
const error = ref("")
const loading = ref(false)

//
// Methods
const close = (): void => {
  emit("close")
}

const fetchQrCode = async (): Promise<void> => {
  url.value = await props.getQrCodeUrl()
}

const verify = async (): Promise<void> => {
  error.value = ""
  loading.value = true

  const success = await props.verifyTwoFactor({ token: token.value })

  if (success) {
    close()
    props.notifyVerified()
  } else {
    error.value = "The provided token is invalid."
  }

  loading.value = false
}

//
// Lifecycle
onMounted(() => {
  fetchQrCode()
  tokenInput.value?.focus()
})

//
// Exposed Test Vars
defineExpose({ url, token, error, loading })
</script>

<template>
  <focus-trap>
    <div
      tabindex="-1"
      class="modal d-block"
    >
      <div class="modal-dialog modal-dialog-centered modal-557px">
        <div class="modal-content _modal_content">
          <div class="modal-header d-block">
            <div class="d-flex">
              <h5 class="modal-title font-weight-bold">
                Get codes from the authenticator app.
              </h5>
            </div>
            <div class="small">
              Download the authenticator app and get your verification code for
              free!
            </div>
          </div>
          <div class="modal-body small">
            <h4>Set up Authenticator</h4>
            <ol>
              <li>
                Get an authenticator app from Google Play or the App Store
              </li>
              <li>In the app, select Set up account.</li>
              <li>
                Choose Scan barcode
                <div class="text-center ml-n5">
                  <i
                    v-if="!url"
                    class="fa fa-spin fa-spinner"
                  />
                  <QRCodeVue
                    v-else
                    class="_qrcode"
                    :value="url"
                    :size="200"
                  />
                </div>
              </li>
              <li>
                Enter the 6-digit code generated by the authenticator app.
                <div class="form-group row mt-2">
                  <label
                    for="token"
                    class="col-sm-3 col-form-label"
                  >
                    Enter code:
                  </label>
                  <div class="col-sm-8">
                    <input
                      id="token"
                      ref="tokenInput"
                      v-model="token"
                      type="text"
                      class="form-control _token"
                      :class="{ 'is-invalid': error }"
                      @keydown.enter="verify"
                    >
                    <ValidationMessage
                      class="_token_error"
                      :message="error"
                    />
                  </div>
                </div>
              </li>
            </ol>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-default _cancel"
              @click="close"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-default _verify"
              :disabled="loading"
              @click="verify"
            >
              Verify
              <i
                v-if="loading"
                class="fa fa-spin fa-spinner ml-1"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  </focus-trap>
</template>
